/**
 * Feature-flag route guard + helpers.
 *
 * Extracted from the monolithic router (architecture review item 10). Used both
 * by the global `beforeEach` guard and by individual route `beforeEnter`/
 * `redirect` definitions that need to gate on a flag.
 */
import featureFlagsConfig from '@/app/feature-flags';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  isFeatureGloballyEnabled,
  isFeatureEnabled as resolveFeatureFlag,
} from '@/utils/featureFlags';

/**
 * Check whether a feature flag is enabled (global config OR user entitlement OR super user).
 * Used in route guards where the useFeatureFlags composable is not available.
 * @param {string} featureKey - e.g. 'dashboard-builder'
 * @returns {boolean}
 */
export function isFeatureFlagEnabled(featureKey) {
  return resolveFeatureFlag(featureKey, featureFlagsConfig, useAuthStore().$state || {});
}

export function isGlobalFeatureFlagEnabled(featureKey) {
  return isFeatureGloballyEnabled(featureKey, featureFlagsConfig, useAuthStore().$state || {});
}

export function isAnyFeatureFlagEnabled(featureKeys) {
  if (!Array.isArray(featureKeys) || featureKeys.length === 0) return true;
  return featureKeys.some((featureKey) => isFeatureFlagEnabled(featureKey));
}

/**
 * Global `beforeEach` guard: enforces `meta.requiresFeatureFlag` and
 * `meta.requiresAnyFeatureFlags`. Behaviour is unchanged from the original
 * inline guard.
 */
export function featureFlagGuard(to) {
  const requiredGlobalFlag = to.meta?.requiresGlobalFeatureFlag;
  if (requiredGlobalFlag && !isGlobalFeatureFlagEnabled(requiredGlobalFlag)) {
    return { name: 'my-environment.overview' };
  }

  const requiredFlag = to.meta?.requiresFeatureFlag;
  if (requiredFlag && !isFeatureFlagEnabled(requiredFlag)) {
    if (requiredFlag === 'api-docs-catalog') {
      return to.path.startsWith('/api-docs-auth') || to.meta?.requiresAuth
        ? { name: 'api-docs-landing-auth' }
        : { name: 'api-docs-landing' };
    }
    return { name: 'home' };
  }

  const requiredAny = to.meta?.requiresAnyFeatureFlags;
  if (requiredAny && !isAnyFeatureFlagEnabled(requiredAny)) {
    return { name: 'home' };
  }

  return true;
}
